import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  IconButton,
  Tooltip,
  Chip,
  CircularProgress,
  Divider,
  LinearProgress,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import GitHubIcon from '@mui/icons-material/GitHub';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useNavigate, useParams } from 'react-router-dom';
import designSystemService from '../../services/designSystemService';
import TokenEditor from './TokenEditor';

// ── Asset drop zone ────────────────────────────────────────────────────────────

const AssetDropZone = ({ dsId, assets = [], onAssetsChange, assetType, accept, label, hint }) => {
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const handleFiles = async (files) => {
    if (!dsId || !files.length) return;
    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        const result = await designSystemService.uploadAsset(dsId, file, assetType);
        onAssetsChange((prev) => {
          const filtered = prev.filter((a) => a.name !== result.name);
          return [...filtered, result];
        });
      }
    } catch (err) {
      console.error('Asset upload failed:', err);
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    handleFiles(e.dataTransfer.files);
  };

  const handleDelete = async (assetName) => {
    if (!dsId) return;
    try {
      await designSystemService.deleteAsset(dsId, assetName);
      onAssetsChange((prev) => prev.filter((a) => a.name !== assetName));
    } catch (err) {
      console.error('Asset delete failed:', err);
    }
  };

  const filtered = assets.filter((a) => a.assetType === assetType);

  return (
    <Box>
      <Box
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
        sx={{
          border: `2px dashed ${dragging ? '#6366f1' : '#ddd'}`,
          borderRadius: 2,
          p: 2.5,
          textAlign: 'center',
          cursor: 'pointer',
          background: dragging ? 'rgba(99,102,241,0.04)' : '#fafafa',
          transition: 'all 0.15s',
          '&:hover': { borderColor: '#aaa', background: '#f5f5f5' },
        }}
      >
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          multiple
          style={{ display: 'none' }}
          onChange={(e) => handleFiles(e.target.files)}
        />
        {uploading ? (
          <CircularProgress size={20} />
        ) : (
          <>
            <CloudUploadIcon sx={{ fontSize: 24, color: '#ccc', mb: 0.5 }} />
            <Typography variant="body2" sx={{ fontSize: 13, color: '#888' }}>{label}</Typography>
            {hint && <Typography variant="caption" sx={{ fontSize: 11, color: '#bbb' }}>{hint}</Typography>}
          </>
        )}
      </Box>

      {filtered.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
          {filtered.map((a) => (
            <Chip
              key={a.name}
              label={a.name}
              size="small"
              onDelete={() => handleDelete(a.name)}
              icon={<CheckCircleIcon sx={{ fontSize: 14, color: '#16a34a' }} />}
              sx={{ fontSize: 11, background: '#f0fdf4', border: '1px solid #bbf7d0' }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

// ── GitHub link row ────────────────────────────────────────────────────────────

const GithubInput = ({ value, onChange }) => {
  const [input, setInput] = useState(value || '');
  const [added, setAdded] = useState(!!value);

  const handleAdd = () => {
    if (!input.trim()) return;
    onChange(input.trim());
    setAdded(true);
  };

  return (
    <Box sx={{ display: 'flex', gap: 1 }}>
      <TextField
        size="small"
        placeholder="https://github.com/your-org/design-system"
        value={input}
        onChange={(e) => { setInput(e.target.value); setAdded(false); }}
        onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
        fullWidth
        sx={{ '& .MuiInputBase-input': { fontSize: 13, py: 0.75 } }}
        InputProps={{
          startAdornment: <GitHubIcon sx={{ fontSize: 18, color: '#aaa', mr: 1 }} />,
        }}
      />
      <Button
        variant="outlined"
        size="small"
        onClick={handleAdd}
        disabled={!input.trim() || added}
        sx={{
          fontSize: 12,
          textTransform: 'none',
          borderColor: '#ddd',
          color: '#555',
          flexShrink: 0,
          '&:hover': { borderColor: '#999' },
        }}
      >
        {added ? 'Added' : 'Add'}
      </Button>
    </Box>
  );
};

// ── DesignSystemSetup ─────────────────────────────────────────────────────────

/**
 * DesignSystemSetup — create or edit a DesignSystem.
 * Route params: :id (optional — if present, edit mode)
 */
const DesignSystemSetup = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [dsId, setDsId] = useState(id || null);

  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [githubUrl, setGithubUrl] = useState('');
  const [notes, setNotes] = useState('');
  const [tokens, setTokens] = useState(null);
  const [assets, setAssets] = useState([]);

  // ── Load existing DS ─────────────────────────────────────────────────────────

  useEffect(() => {
    if (!isEdit) return;
    const load = async () => {
      try {
        const data = await designSystemService.getDesignSystem(id);
        if (data) {
          setName(data.name || '');
          setCompany(data.company || '');
          setGithubUrl(data.githubUrl || '');
          setNotes(data.notes || '');
          setTokens(data.tokens || null);
          setAssets(data.assets || []);
        }
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id, isEdit]);

  // ── Auto-create DS record so assets can be uploaded immediately ──────────────

  const ensureDsId = async () => {
    if (dsId) return dsId;
    const result = await designSystemService.createDesignSystem({
      name: name || 'Untitled Design System',
      company,
      notes,
      githubUrl,
    });
    setDsId(result.id);
    return result.id;
  };

  // ── Save ─────────────────────────────────────────────────────────────────────

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      const payload = { name, company, githubUrl, notes, tokens };
      if (dsId) {
        await designSystemService.updateDesignSystem(dsId, payload);
      } else {
        const result = await designSystemService.createDesignSystem(payload);
        setDsId(result.id);
      }
      navigate('/design-studio/design-systems');
    } catch (err) {
      console.error('Save failed:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleTokenSave = async (newTokens) => {
    setTokens(newTokens);
    if (dsId) {
      try {
        await designSystemService.updateDesignSystem(dsId, { tokens: newTokens });
      } catch (err) {
        console.error('Token auto-save failed:', err);
      }
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 4 }}>
        <LinearProgress sx={{ borderRadius: 1 }} />
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', overflowY: 'auto', background: '#fff' }}>
      <Box sx={{ maxWidth: 720, mx: 'auto', px: 4, py: 4 }}>

        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <IconButton
              size="small"
              onClick={() => navigate('/design-studio/design-systems')}
              sx={{ color: '#666' }}
            >
              <ArrowBackIcon fontSize="small" />
            </IconButton>
            <div>
              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: 20, color: '#1a1a1a', lineHeight: 1.2 }}>
                {isEdit ? 'Edit design system' : 'Set up your design system'}
              </Typography>
              <Typography variant="body2" sx={{ color: '#888', fontSize: 13 }}>
                Configure brand tokens and assets for AI generation
              </Typography>
            </div>
          </Box>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!name.trim() || saving}
            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
            sx={{
              background: '#e85d26',
              color: '#fff',
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: 13,
              px: 2.5,
              '&:hover': { background: '#cf4f1e' },
            }}
          >
            {saving ? 'Saving…' : 'Continue to generation'}
          </Button>
        </Box>

        {/* Name */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: 13, color: '#1a1a1a', mb: 0.75 }}>
            Design system name
          </Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="Acme Design System"
            value={name}
            onChange={(e) => setName(e.target.value)}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, fontSize: 14 } }}
          />
        </Box>

        {/* Company blurb */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: 13, color: '#1a1a1a', mb: 0.5 }}>
            Company name and blurb
          </Typography>
          <Typography variant="caption" sx={{ color: '#888', fontSize: 12, display: 'block', mb: 0.75 }}>
            Describe what your product does. Claude will use this for realistic copy.
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={3}
            placeholder="Acme Corp — fast-casual pasta restaurant with a mobile ordering app. Warm, earthy brand, playful but professional."
            value={company}
            onChange={(e) => setCompany(e.target.value)}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, fontSize: 13 } }}
          />
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Design examples section */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: 14, color: '#1a1a1a', mb: 0.5 }}>
            Provide examples of your design system and products
          </Typography>
          <Typography variant="caption" sx={{ color: '#888', fontSize: 12, display: 'block', mb: 2 }}>
            Claude will study these to match your visual style.
          </Typography>

          {/* GitHub */}
          <Paper
            elevation={0}
            sx={{ border: '1px solid #eee', borderRadius: 2, p: 2, mb: 2 }}
          >
            <Typography variant="body2" sx={{ fontSize: 13, fontWeight: 500, color: '#333', mb: 1 }}>
              Link code on GitHub
            </Typography>
            <GithubInput value={githubUrl} onChange={setGithubUrl} />
          </Paper>

          {/* Fonts, logos, assets upload */}
          <Paper
            elevation={0}
            sx={{ border: '1px solid #eee', borderRadius: 2, p: 2, mb: 2 }}
          >
            <Typography variant="body2" sx={{ fontSize: 13, fontWeight: 500, color: '#333', mb: 1 }}>
              Add fonts, logos and assets
            </Typography>
            <AssetDropZone
              dsId={dsId}
              assets={assets}
              onAssetsChange={setAssets}
              assetType="font"
              accept=".ttf,.otf,.woff,.woff2"
              label="Drop font files here"
              hint=".ttf  .otf  .woff  .woff2"
            />
            <Box sx={{ mt: 1.5 }}>
              <AssetDropZone
                dsId={dsId}
                assets={assets}
                onAssetsChange={setAssets}
                assetType="logo"
                accept=".svg,.png,.jpg,.jpeg"
                label="Drop logos or brand images here"
                hint=".svg  .png  .jpg"
              />
            </Box>
          </Paper>

          {/* Figma placeholder */}
          <Paper
            elevation={0}
            sx={{
              border: '1px dashed #eee',
              borderRadius: 2,
              p: 2,
              background: '#fafafa',
              opacity: 0.7,
            }}
          >
            <Typography variant="body2" sx={{ fontSize: 13, fontWeight: 500, color: '#aaa', mb: 0.5 }}>
              Upload a .fig file
            </Typography>
            <Typography variant="caption" sx={{ color: '#bbb', fontSize: 11 }}>
              Figma integration coming soon
            </Typography>
          </Paper>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Token editor */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: 14, color: '#1a1a1a', mb: 0.5 }}>
            Design tokens
          </Typography>
          <Typography variant="caption" sx={{ color: '#888', fontSize: 12, display: 'block', mb: 2 }}>
            Edit colors, typography, spacing, and radii. Changes auto-save.
          </Typography>
          <TokenEditor tokens={tokens} onSave={handleTokenSave} />
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Notes */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: 13, color: '#1a1a1a', mb: 0.5 }}>
            Any other notes?
          </Typography>
          <Typography variant="caption" sx={{ color: '#888', fontSize: 12, display: 'block', mb: 0.75 }}>
            Tone of voice, do/don'ts, references, anything else Claude should know.
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={3}
            placeholder="Rounded corners everywhere. Bold typography. Never use blue for primary actions — orange only."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, fontSize: 13 } }}
          />
        </Box>

        {/* Bottom save */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!name.trim() || saving}
            startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
            sx={{
              background: '#e85d26',
              color: '#fff',
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              fontSize: 14,
              px: 3,
              py: 1,
              '&:hover': { background: '#cf4f1e' },
            }}
          >
            {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Continue to generation'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default DesignSystemSetup;
